import { DigitalDossierRepresentantInformationSerializer } from '@bcba/serializers';
import { FormSection } from '../../DigitalDossier/DigitalDossierSection/components/FormSection';
import { useTranslation } from '@bcba/react-hooks';
import {
  Card,
  Divider,
  DropzoneReadonly,
  FiFileText,
  FormProvider,
  Grid,
  GridItem,
  ReadOnlyField,
  Stack,
  useForm,
} from '@agrotoken-tech/ui';
import {
  formatDateToShortFormat,
  unixTimestampToDate,
} from '@bcba/common-utils';
import { downloadDocument } from '../../../utils/downloadDocument.utils';
import { ReadOnlyYesNoRadio } from '../../DigitalDossier/components/ReadOnlyYesNoRadio';
import { YES_NO_OPTIONS } from '@bcba/types';

type Props = {
  data: DigitalDossierRepresentantInformationSerializer[];
};

export const ReadOnlyRepresentantsInfoSection = ({ data }: Props) => {
  const { t } = useTranslation();

  // I need to use a FormProvider because our Radios uses rhf, we should export standard Radio from UI
  const formMethods = useForm();

  return (
    <FormProvider {...formMethods}>
      <FormSection title={t('common.legalRepresentatives')}>
        {data.map((repInfo, index) => {
          const { identityDocumentType, position, productCaps, currencyCaps } =
            repInfo ?? {};
          const docInDb = repInfo?.representantDocuments[0]; // TODO: we should receive the document in the serializer, not an array
          const docExpirationDate = docInDb?.document.expirationDate;
          const hasExchangeCap = Boolean(
            repInfo?.powerOfAttorneyExchanges?.length,
          );
          const hasProductCap = Boolean(repInfo?.productCaps.length);
          const hasCurrencyCap = Boolean(repInfo?.currencyCaps.length);
          return (
            <FormSection
              key={repInfo.uuid}
              title={t(
                'digitalDossier.updateLegalRepresentantivesInfo.representativeSection.title',
              ).replace('{{num}}', String(index + 1))}
            >
              <Grid templateColumns={{ md: 'repeat(2, 1fr)' }} gap={6}>
                <ReadOnlyField
                  label={t('common.name')}
                  value={repInfo?.firstName ?? '-'}
                />
                <ReadOnlyField
                  label={t('common.lastName')}
                  value={repInfo?.lastName ?? '-'}
                />
                <ReadOnlyField
                  label={t('common.documentType')}
                  value={
                    identityDocumentType
                      ? t(`identityDocumentType.${identityDocumentType}`)
                      : '-'
                  }
                />
                <ReadOnlyField
                  label={t('common.document')}
                  value={repInfo?.identityDocumentId ?? '-'}
                />
                <ReadOnlyField
                  label={t('common.email')}
                  value={repInfo?.email ?? '-'}
                />
                <ReadOnlyField
                  label={t('common.position')}
                  value={
                    position ? t(`representativePosition.${position}`) : '-'
                  }
                />
                <ReadOnlyField
                  label={t('common.startDateOfTerm')}
                  value={
                    repInfo?.startOfTerm
                      ? formatDateToShortFormat(
                          unixTimestampToDate(repInfo.startOfTerm),
                        )
                      : '-'
                  }
                />
                <ReadOnlyField
                  label={t('common.endDateOfTerm')}
                  value={
                    repInfo?.endOfTerm
                      ? formatDateToShortFormat(
                          unixTimestampToDate(repInfo.endOfTerm),
                        )
                      : '-'
                  }
                />
                {docInDb && (
                  <GridItem colSpan={{ base: 1, md: 2 }}>
                    <DropzoneReadonly
                      fileName={docInDb.document.originalFileName}
                      label={t(
                        'explorer.digitalDossier.updateLegalRepresentantivesInfo.labels.idDocument',
                      )}
                      onClick={() => downloadDocument(docInDb.document.uuid)}
                      leftIcon={FiFileText}
                    />
                  </GridItem>
                )}
                <ReadOnlyField
                  label={t('common.documentIdentifier')}
                  value={docInDb?.document.identifier ?? '-'}
                />
                <ReadOnlyField
                  label={t('common.dueDate')}
                  value={
                    docExpirationDate
                      ? formatDateToShortFormat(
                          unixTimestampToDate(docExpirationDate),
                        )
                      : '-'
                  }
                />
                <ReadOnlyField
                  label={t(
                    'explorer.digitalDossier.updateLegalRepresentantivesInfo.labels.idOfTheAuthorizedLegalRepresentationDocument',
                  )}
                  value={docInDb?.bcAuthorizedDocumentIdentifier ?? '-'}
                />
                <GridItem />
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Card>
                    <FormSection
                      title={t(
                        'digitalDossier.legalRepresentatives.representativeCaps',
                      )}
                    >
                      <Stack gap={8} divider={<Divider />}>
                        <Grid
                          templateColumns={{ md: 'repeat(2, 1fr)' }}
                          gap={6}
                        >
                          <ReadOnlyYesNoRadio
                            label={t('common.powerByBolsa')}
                            defaultValue={
                              hasExchangeCap
                                ? YES_NO_OPTIONS.YES
                                : YES_NO_OPTIONS.NO
                            }
                          />
                          {hasExchangeCap && (
                            <ReadOnlyField
                              label={'Bolsa'}
                              value={repInfo.powerOfAttorneyExchanges
                                .map((e) => t(`powerOfAttorneyExchange.${e}`))
                                .join(', ')}
                            />
                          )}
                        </Grid>
                        <Grid
                          templateColumns={{ md: 'repeat(2, 1fr)' }}
                          gap={6}
                        >
                          <GridItem colSpan={{ base: 1, md: 2 }}>
                            <ReadOnlyYesNoRadio
                              label={t(
                                'explorer.digitalDossier.updateLegalRepresentantivesInfo.labels.hasProductCap',
                              )}
                              defaultValue={
                                hasProductCap
                                  ? YES_NO_OPTIONS.YES
                                  : YES_NO_OPTIONS.NO
                              }
                            />
                          </GridItem>
                          {hasProductCap &&
                            productCaps?.map(({ product, cap }) => (
                              <>
                                <ReadOnlyField
                                  label={t('common.product')}
                                  value={t(`product.${product}`)}
                                />
                                <ReadOnlyField
                                  label={t('common.tons')}
                                  value={String(cap)}
                                />
                              </>
                            ))}
                        </Grid>
                        <Grid
                          templateColumns={{ md: 'repeat(2, 1fr)' }}
                          gap={6}
                        >
                          <GridItem colSpan={{ base: 1, md: 2 }}>
                            <ReadOnlyYesNoRadio
                              label={t(
                                'explorer.digitalDossier.updateLegalRepresentantivesInfo.labels.hasCurrencyCap',
                              )}
                              defaultValue={
                                hasCurrencyCap
                                  ? YES_NO_OPTIONS.YES
                                  : YES_NO_OPTIONS.NO
                              }
                            />
                          </GridItem>
                          {hasCurrencyCap &&
                            currencyCaps?.map(({ currency, cap }) => (
                              <>
                                <ReadOnlyField
                                  label={t('common.currency')}
                                  value={t(`currency.${currency}`)}
                                />
                                <ReadOnlyField
                                  label={t('common.amount')}
                                  value={String(cap)}
                                />
                              </>
                            ))}
                        </Grid>
                      </Stack>
                    </FormSection>
                  </Card>
                </GridItem>
              </Grid>
            </FormSection>
          );
        })}
      </FormSection>
    </FormProvider>
  );
};
